import React, { useState, useEffect } from 'react';
import { 
  User, 
  Mail, 
  Phone, 
  Calendar, 
  Shield, 
  Key, 
  Save,
  Check,
  AlertCircle,
  RefreshCw,
  Edit as EditIcon
} from 'lucide-react';
import { useSelector } from 'react-redux';
import AdminApi from '../../apis/adminApis';
import ProfileEditForm from './ProfileEditForm';

interface ProfileData {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone_number?: string;
  role: string;
  created_at: string;
  avatar?: string;
  avatar_url?: string;
}

const AdminProfile: React.FC = () => {
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [editMode, setEditMode] = useState<boolean>(false);
  
  // Password change states
  const [showPasswordForm, setShowPasswordForm] = useState<boolean>(false);
  const [currentPassword, setCurrentPassword] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [passwordErrors, setPasswordErrors] = useState<{[key: string]: string}>({});
  const [passwordLoading, setPasswordLoading] = useState<boolean>(false);
  
  // Get user data from Redux store (for initial display before API call completes)
  const userLoginData = useSelector((state: any) => state.data.login.value);
  
  // Fetch profile data
  const fetchProfile = async () => {
    setLoading(true);
    try {
      const response = await AdminApi.getProfile();
      
      if (response.data.status === 'success') {
        setProfile(response.data.data);
        setError(null);
      } else {
        setError('Failed to load profile data');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error loading profile');
      console.error('Error fetching profile:', err);
    } finally {
      setLoading(false);
    }
  };
  
  // Initial load
  useEffect(() => {
    fetchProfile();
  }, []);
  
  // Format date for display
  const formatDate = (dateString: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  
  // Handle profile update success
  const handleProfileUpdateSuccess = (updatedProfile: any) => {
    setProfile(updatedProfile);
    setEditMode(false);
    setSuccessMessage('Profile updated successfully');
    
    // Clear success message after a few seconds
    setTimeout(() => {
      setSuccessMessage(null);
    }, 5000);
  };
  
  // Handle password change
  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    const errors: {[key: string]: string} = {};
    
    if (!currentPassword) {
      errors.currentPassword = 'Current password is required';
    }
    
    if (!newPassword) {
      errors.newPassword = 'New password is required';
    } else if (newPassword.length < 8) {
      errors.newPassword = 'Password must be at least 8 characters';
    }
    
    if (newPassword !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    if (Object.keys(errors).length > 0) {
      setPasswordErrors(errors);
      return;
    }
    
    // Submit password change request
    setPasswordLoading(true);
    try {
      const response = await AdminApi.changePassword({
        current_password: currentPassword,
        new_password: newPassword,
        new_password_confirmation: confirmPassword
      });
      
      if (response.data.status === 'success') {
        setSuccessMessage('Password changed successfully');
        setShowPasswordForm(false);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setPasswordErrors({});
        
        // Clear success message after a few seconds
        setTimeout(() => {
          setSuccessMessage(null);
        }, 5000);
      } else {
        setPasswordErrors({
          general: response.data.message || 'Failed to change password'
        });
      }
    } catch (err: any) {
      const responseErrors = err.response?.data?.errors;
      
      if (responseErrors) {
        // Format Laravel validation errors
        const formattedErrors: {[key: string]: string} = {};
        Object.keys(responseErrors).forEach(key => {
          const fieldName = key === 'current_password' ? 'currentPassword' : 
                           key === 'new_password' ? 'newPassword' : 
                           key === 'new_password_confirmation' ? 'confirmPassword' : key;
          formattedErrors[fieldName] = responseErrors[key][0];
        });
        setPasswordErrors(formattedErrors);
      } else {
        setPasswordErrors({
          general: err.response?.data?.message || 'Error changing password. Please try again.'
        });
      }
      console.error('Error changing password:', err);
    } finally {
      setPasswordLoading(false);
    }
  };
  
  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white rounded-lg shadow px-6 py-4">
        <h1 className="text-xl font-semibold text-gray-800">My Profile</h1>
        <p className="text-gray-600">View and manage your account information</p>
      </div>
      
      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-md">
          <div className="flex items-center">
            <Check className="h-5 w-5 text-green-500 mr-2" />
            <p className="text-sm text-green-700">{successMessage}</p>
          </div>
        </div>
      )}
      
      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-md">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}
      
      {loading ? (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-center items-center h-64">
            <RefreshCw className="h-8 w-8 text-gray-400 animate-spin" />
          </div>
        </div>
      ) : profile ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {editMode ? (
            // Edit Profile Form
            <div className="col-span-1 md:col-span-3">
              <ProfileEditForm 
                profile={profile}
                onSuccess={handleProfileUpdateSuccess}
                onCancel={() => setEditMode(false)}
              />
            </div>
          ) : (
            <>
              {/* Profile Card */}
              <div className="bg-white rounded-lg shadow col-span-1">
                <div className="px-6 py-5 border-b border-gray-200 flex justify-between items-center">
                  <h2 className="text-lg font-semibold text-gray-800">Profile</h2>
                  <button
                    onClick={() => setEditMode(true)}
                    className="text-orange-600 hover:text-orange-800 flex items-center text-sm font-medium"
                  >
                    <EditIcon className="h-4 w-4 mr-1" />
                    Edit
                  </button>
                </div>
                <div className="p-6 flex flex-col items-center">
                  <div className="w-32 h-32 rounded-full bg-orange-500 flex items-center justify-center text-white text-4xl font-bold mb-4">
                    {profile.avatar_url ? (
                      <img 
                        src={profile.avatar_url} 
                        alt={`${profile.first_name} ${profile.last_name}`} 
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      <span>{profile.first_name.charAt(0)}{profile.last_name.charAt(0)}</span>
                    )}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900">{profile.first_name} {profile.last_name}</h3>
                  <p className="text-gray-600 mt-1">{profile.email}</p>
                  <span className="mt-2 inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                    <Shield className="h-4 w-4 mr-1" />
                    {profile.role}
                  </span>
                  
                  <div className="w-full mt-6 space-y-3">
                    {profile.phone_number && (
                      <div className="flex items-center">
                        <Phone className="h-5 w-5 text-gray-400 mr-3" />
                        <span className="text-gray-700">{profile.phone_number}</span>
                      </div>
                    )}
                    
                    <div className="flex items-center">
                      <Calendar className="h-5 w-5 text-gray-400 mr-3" />
                      <span className="text-gray-700">Joined {formatDate(profile.created_at)}</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Security Settings */}
              <div className="bg-white rounded-lg shadow col-span-1 md:col-span-2">
                <div className="px-6 py-5 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-800">Security</h2>
                </div>
                <div className="p-6">
                  <div className="mb-6">
                    <h3 className="text-md font-medium text-gray-700">Account Security</h3>
                    <p className="text-sm text-gray-500 mt-1">Manage your account password and security settings</p>
                  </div>
                  
                  <div className="bg-gray-50 p-4 rounded-lg flex items-center justify-between">
                    <div className="flex items-center">
                      <Key className="h-5 w-5 text-gray-400 mr-3" />
                      <div>
                        <p className="text-sm font-medium text-gray-700">Password</p>
                        <p className="text-xs text-gray-500">Last changed: Unknown</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowPasswordForm(!showPasswordForm)}
                      className="text-sm text-orange-600 hover:text-orange-800 font-medium"
                    >
                      {showPasswordForm ? 'Cancel' : 'Change Password'}
                    </button>
                  </div>
                  
                  {showPasswordForm && (
                    <div className="mt-6 bg-white border border-gray-200 rounded-lg p-4">
                      <h4 className="text-md font-medium text-gray-700 mb-4">Change Password</h4>
                      
                      {/* Password change form */}
                      <form onSubmit={handlePasswordChange} className="space-y-4">
                        {passwordErrors.general && (
                          <div className="bg-red-50 border-l-4 border-red-500 p-3 rounded">
                            <p className="text-sm text-red-700">{passwordErrors.general}</p>
                          </div>
                        )}
                        
                        <div>
                          <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700">
                            Current Password
                          </label>
                          <input
                            type="password"
                            id="currentPassword"
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            className={`mt-1 block w-full rounded-md border ${
                              passwordErrors.currentPassword ? 'border-red-300' : 'border-gray-300'
                            } shadow-sm py-2 px-3 focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm`}
                          />
                          {passwordErrors.currentPassword && (
                            <p className="mt-1 text-sm text-red-600">{passwordErrors.currentPassword}</p>
                          )}
                        </div>
                        
                        <div>
                          <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                            New Password
                          </label>
                          <input
                            type="password"
                            id="newPassword"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            className={`mt-1 block w-full rounded-md border ${
                              passwordErrors.newPassword ? 'border-red-300' : 'border-gray-300'
                            } shadow-sm py-2 px-3 focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm`}
                          />
                          {passwordErrors.newPassword && (
                            <p className="mt-1 text-sm text-red-600">{passwordErrors.newPassword}</p>
                          )}
                        </div>
                        
                        <div>
                          <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                            Confirm New Password
                          </label>
                          <input
                            type="password"
                            id="confirmPassword"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            className={`mt-1 block w-full rounded-md border ${
                              passwordErrors.confirmPassword ? 'border-red-300' : 'border-gray-300'
                            } shadow-sm py-2 px-3 focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm`}
                          />
                          {passwordErrors.confirmPassword && (
                            <p className="mt-1 text-sm text-red-600">{passwordErrors.confirmPassword}</p>
                          )}
                        </div>
                        
                        <div className="flex justify-end">
                          <button
                            type="submit"
                            disabled={passwordLoading}
                            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                          >
                            {passwordLoading ? (
                              <>
                                <RefreshCw className="animate-spin -ml-1 mr-2 h-4 w-4" />
                                Updating...
                              </>
                            ) : (
                              <>
                                <Save className="-ml-1 mr-2 h-4 w-4" />
                                Update Password
                              </>
                            )}
                          </button>
                        </div>
                      </form>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default AdminProfile;